import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
} from '@mui/material';
import React, { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import Text from 'commons/Text';
import { getTextEdition } from 'utils/utils';

import useStyles from './styles';

const DownloadCertificateLanguageDialog = ({
  isOpen,
  selectedLanguage,
  selectedEdition,
  // null  = editions data not available in this context (skip edition field)
  // []    = editions loaded, user owns none
  // [...] = editions owned by user
  ownedEditions = null,
  onClose,
  onChangeLanguage,
  onChangeEdition,
  onConfirm,
  isDownloading,
}) => {
  const classes = useStyles();
  const { t } = useTranslation();

  const editionsLoaded = ownedEditions !== null;
  const normalizedEditions = useMemo(() => ownedEditions || [], [ownedEditions]);
  const hasSingleEdition = normalizedEditions.length === 1;

  // Auto-select when only one edition is owned
  useEffect(() => {
    if (hasSingleEdition && !selectedEdition && onChangeEdition) {
      onChangeEdition(normalizedEditions[0]);
    }
  }, [hasSingleEdition, selectedEdition, normalizedEditions, onChangeEdition]);

  const isDownloadDisabled = useMemo(() => {
    if (isDownloading) return true;
    if (editionsLoaded && normalizedEditions.length > 1 && !selectedEdition) return true;
    if (!selectedLanguage) return true;
    return false;
  }, [isDownloading, editionsLoaded, normalizedEditions.length, selectedEdition, selectedLanguage]);

  const renderEditionField = () => {
    if (!editionsLoaded || normalizedEditions.length === 0) return null;

    const totalEdition = normalizedEditions[0]?.total_edition ?? selectedEdition?.total_edition;

    const isSingleOneOfOne =
      hasSingleEdition &&
      Number(normalizedEditions[0]?.edition_number) === 1 &&
      Number(totalEdition) === 1;

    if (isSingleOneOfOne) {
      return null;
    }

    if (hasSingleEdition) {
      return (
        <FormControl
          fullWidth
          className={classes.formControl}
        >
          <InputLabel shrink>{t('edition')}</InputLabel>
          <Text className={classes.readOnlyEdition}>
            {getTextEdition(normalizedEditions[0]?.edition_number, totalEdition)}
          </Text>
        </FormControl>
      );
    }

    return (
      <FormControl
        fullWidth
        className={classes.formControl}
      >
        <InputLabel>{t('edition')}</InputLabel>
        <Select
          value={selectedEdition?.id || ''}
          onChange={(e) => {
            const edition = normalizedEditions.find((ed) => ed.id === e.target.value);
            onChangeEdition(edition);
          }}
          label={t('edition')}
        >
          {normalizedEditions.map((edition) => (
            <MenuItem
              key={edition.id}
              value={edition.id}
            >
              {getTextEdition(edition.edition_number, totalEdition)}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    );
  };

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      classes={{
        paper: classes.dialogPaper,
      }}
    >
      <DialogTitle className={classes.dialogTitle}>
        <Text fontWeightBold>{t('download_certificate')}</Text>
      </DialogTitle>

      <DialogContent className={classes.dialogContent}>
        {renderEditionField()}

        <FormControl
          fullWidth
          className={classes.formControl}
        >
          <Text className={classes.fieldLabel}>{t('language')}</Text>
          <RadioGroup
            value={selectedLanguage}
            onChange={(event) => onChangeLanguage(event.target.value)}
          >
            <FormControlLabel
              value="en"
              control={<Radio />}
              label={<Text>{t('english')}</Text>}
            />
            <FormControlLabel
              value="vi"
              control={<Radio />}
              label={<Text>{t('vietnamese')}</Text>}
            />
            <FormControlLabel
              value="both"
              control={<Radio />}
              label={<Text>{t('both')}</Text>}
            />
          </RadioGroup>
        </FormControl>
      </DialogContent>

      <DialogActions className={classes.dialogActions}>
        <Button
          variant="outlined"
          onClick={onClose}
          disabled={isDownloading}
        >
          <Text>{t('cancel')}</Text>
        </Button>

        <Button
          variant="contained"
          color="primary"
          onClick={onConfirm}
          disabled={isDownloadDisabled}
        >
          <Text>{t('download')}</Text>
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DownloadCertificateLanguageDialog;
